import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, FormEvent } from "react";

// =============================================================================
// Brand Colors
// =============================================================================

const BRAND_COLORS = {
  primary: "#DCA0FA", // Plasma Violet
  accent: "#E6BEFA", // Aurora Lilac
  bgDeep: "#14143C",
  bgSurface: "#281E46",
};

// =============================================================================
// Footer Links Data
// =============================================================================

const FOOTER_LINKS = {
  product: {
    title: "Product",
    links: [
      { label: "Features", href: "#" },
      { label: "Pricing", href: "#" },
      { label: "Documentation", href: "#" },
      { label: "Console", href: "#" },
      { label: "API Reference", href: "#" },
    ],
  },
  company: {
    title: "Company",
    links: [
      { label: "About", href: "#" },
      { label: "Blog", href: "#" },
      { label: "Careers", href: "#" },
      { label: "Press Kit", href: "#" },
    ],
  },
  resources: {
    title: "Resources",
    links: [
      { label: "GitHub", href: "https://github.com" },
      { label: "Discord", href: "#" },
      { label: "Twitter", href: "#" },
      { label: "Telegram", href: "https://t.me/hvme" },
    ],
  },
  legal: {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "#" },
      { label: "Terms of Service", href: "#" },
      { label: "Security", href: "#" },
      { label: "DPA", href: "#" },
    ],
  },
};

// =============================================================================
// Animation Variants
// =============================================================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const columnVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const linkVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
    },
  },
};

// =============================================================================
// Social Icons with Hover Animations
// =============================================================================

function SocialIcons() {
  const socials = [
    {
      name: "GitHub",
      href: "#",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
        </svg>
      ),
    },
    {
      name: "Discord",
      href: "#",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
        </svg>
      ),
    },
    {
      name: "Twitter",
      href: "#",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    },
    {
      name: "Telegram",
      href: "https://t.me/hvme",
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="flex items-center gap-4">
      {socials.map((social) => (
        <motion.a
          key={social.name}
          href={social.href}
          className="relative p-2 rounded-lg text-[#E6BEFA]/60 transition-colors duration-300"
          aria-label={social.name}
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.95 }}
          style={{
            transition: "all 0.3s ease",
          }}
          onHoverStart={(e) => {
            const target = e.target as HTMLElement;
            target.style.color = BRAND_COLORS.primary;
            target.style.boxShadow = `0 0 20px ${BRAND_COLORS.primary}40, 0 0 40px ${BRAND_COLORS.primary}20`;
            target.style.background = `${BRAND_COLORS.primary}15`;
          }}
          onHoverEnd={(e) => {
            const target = e.target as HTMLElement;
            target.style.color = "";
            target.style.boxShadow = "none";
            target.style.background = "transparent";
          }}
        >
          {social.icon}
        </motion.a>
      ))}
    </div>
  );
}

// =============================================================================
// Back to Top Button
// =============================================================================

function BackToTopButton() {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <motion.button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 z-50 p-3 rounded-full border border-[#DCA0FA]/30 backdrop-blur-sm"
      style={{
        background: `linear-gradient(135deg, ${BRAND_COLORS.bgSurface}, ${BRAND_COLORS.bgDeep})`,
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{
        scale: 1.1,
        boxShadow: `0 0 30px ${BRAND_COLORS.primary}50, 0 0 60px ${BRAND_COLORS.primary}25`,
      }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.3 }}
      aria-label="Back to top"
    >
      <motion.svg
        className="w-5 h-5"
        fill="none"
        stroke={BRAND_COLORS.primary}
        strokeWidth={2}
        viewBox="0 0 24 24"
        animate={{ y: [0, -2, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </motion.svg>
    </motion.button>
  );
}

// =============================================================================
// Animated Newsletter Form
// =============================================================================

type FormStatus = "idle" | "loading" | "success" | "error";

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      setStatus("error");
      setErrorMessage("Please enter a valid email address");
      return;
    }

    setStatus("loading");

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Simulate success (in real implementation, this would be an API call)
    const isSuccess = Math.random() > 0.1; // 90% success rate for demo

    if (isSuccess) {
      setStatus("success");
      setEmail("");
      setTimeout(() => setStatus("idle"), 4000);
    } else {
      setStatus("error");
      setErrorMessage("Something went wrong. Please try again.");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full md:w-auto">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 md:w-72">
          <motion.input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            disabled={status === "loading" || status === "success"}
            className="w-full px-4 py-3 rounded-lg border text-sm placeholder-[#E6BEFA]/40 focus:outline-none transition-all duration-300 disabled:opacity-50"
            style={{
              background: BRAND_COLORS.bgSurface,
              borderColor: status === "error" ? "#ef4444" : `${BRAND_COLORS.primary}30`,
              color: "#fff",
            }}
            whileFocus={{
              borderColor: BRAND_COLORS.primary,
              boxShadow: `0 0 20px ${BRAND_COLORS.primary}20`,
            }}
          />
          <AnimatePresence>
            {status === "error" && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute -bottom-6 left-0 text-xs text-red-400"
              >
                {errorMessage}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <motion.button
          type="submit"
          disabled={status === "loading" || status === "success"}
          className="relative px-6 py-3 rounded-lg text-sm font-medium overflow-hidden disabled:cursor-not-allowed"
          style={{
            background:
              status === "success"
                ? "linear-gradient(135deg, #22c55e, #16a34a)"
                : `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.accent})`,
            color: status === "success" ? "#fff" : BRAND_COLORS.bgDeep,
          }}
          whileHover={
            status === "idle"
              ? {
                  scale: 1.02,
                  boxShadow: `0 0 30px ${BRAND_COLORS.primary}50`,
                }
              : {}
          }
          whileTap={status === "idle" ? { scale: 0.98 } : {}}
        >
          <AnimatePresence mode="wait">
            {status === "loading" && (
              <motion.span
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center justify-center gap-2"
              >
                <motion.span
                  className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                />
                Subscribing...
              </motion.span>
            )}
            {status === "success" && (
              <motion.span
                key="success"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <motion.path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.4 }}
                  />
                </svg>
                Subscribed!
              </motion.span>
            )}
            {(status === "idle" || status === "error") && (
              <motion.span
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                Subscribe
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      </div>
    </form>
  );
}

// =============================================================================
// Animated Gradient Divider
// =============================================================================

function GradientDivider() {
  return (
    <div className="relative h-px w-full overflow-hidden">
      <motion.div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(90deg, transparent, ${BRAND_COLORS.primary}60, ${BRAND_COLORS.accent}60, transparent)`,
        }}
        animate={{
          x: ["-100%", "100%"],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "linear",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(90deg, transparent, ${BRAND_COLORS.primary}20, ${BRAND_COLORS.accent}20, transparent)`,
        }}
      />
    </div>
  );
}

// =============================================================================
// Footer Link Item with Hover Animation
// =============================================================================

interface FooterLinkProps {
  href: string;
  label: string;
}

function FooterLink({ href, label }: FooterLinkProps) {
  return (
    <motion.li variants={linkVariants}>
      <motion.a
        href={href}
        className="relative inline-block font-body text-sm transition-colors duration-300"
        style={{ color: `${BRAND_COLORS.accent}70` }}
        whileHover={{ x: 4 }}
        onHoverStart={(e) => {
          const target = e.target as HTMLElement;
          target.style.color = BRAND_COLORS.primary;
        }}
        onHoverEnd={(e) => {
          const target = e.target as HTMLElement;
          target.style.color = `${BRAND_COLORS.accent}70`;
        }}
      >
        {label}
      </motion.a>
    </motion.li>
  );
}

// =============================================================================
// Footer Component
// =============================================================================

export function Footer() {
  const footerRef = useRef<HTMLElement>(null);
  const isInView = useInView(footerRef, { once: true, margin: "-100px" });

  return (
    <footer
      ref={footerRef}
      className="relative border-t overflow-hidden"
      style={{
        borderColor: `${BRAND_COLORS.primary}20`,
        background: BRAND_COLORS.bgDeep,
      }}
    >
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div
          className="absolute -top-40 -left-40 w-80 h-80 rounded-full blur-3xl opacity-20"
          style={{
            background: `radial-gradient(circle, ${BRAND_COLORS.primary}40, transparent 70%)`,
          }}
        />
        <div
          className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full blur-3xl opacity-15"
          style={{
            background: `radial-gradient(circle, ${BRAND_COLORS.accent}30, transparent 70%)`,
          }}
        />

        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(${BRAND_COLORS.primary} 1px, transparent 1px), linear-gradient(90deg, ${BRAND_COLORS.primary} 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-12 sm:py-16">
        {/* Main Footer Grid */}
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-6 mb-12"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Logo & Description */}
          <motion.div className="col-span-2" variants={columnVariants}>
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                className="h-10 w-10 rounded-lg flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.accent})`,
                }}
                whileHover={{
                  scale: 1.05,
                  boxShadow: `0 0 30px ${BRAND_COLORS.primary}50`,
                }}
              >
                <span className="text-sm font-bold" style={{ color: BRAND_COLORS.bgDeep }}>
                  H
                </span>
              </motion.div>
              <span className="font-nav text-lg font-bold tracking-wider text-white">HVME</span>
            </div>
            <p
              className="font-body text-sm mb-6 max-w-xs leading-relaxed"
              style={{ color: `${BRAND_COLORS.accent}70` }}
            >
              Sovereign AI Infrastructure. FHE-native, AI-aware, privacy by design.
            </p>
            <SocialIcons />
          </motion.div>

          {/* Link Columns */}
          {Object.values(FOOTER_LINKS).map((section, index) => (
            <motion.div
              key={section.title}
              variants={columnVariants}
              custom={index}
              className="col-span-1"
            >
              <h4
                className="font-nav text-xs font-bold uppercase tracking-widest mb-4"
                style={{ color: BRAND_COLORS.primary }}
              >
                {section.title}
              </h4>
              <motion.ul
                className="space-y-3"
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                {section.links.map((link) => (
                  <FooterLink key={link.label} href={link.href} label={link.label} />
                ))}
              </motion.ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Animated Divider */}
        <GradientDivider />

        {/* Newsletter */}
        <motion.div
          className="py-8 sm:py-10"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h4 className="font-heading font-semibold text-white mb-1 text-lg">Stay updated</h4>
              <p className="font-body text-sm" style={{ color: `${BRAND_COLORS.accent}70` }}>
                Get the latest news and updates from HVME.
              </p>
            </div>
            <NewsletterForm />
          </div>
        </motion.div>

        {/* Animated Divider */}
        <GradientDivider />

        {/* Bottom Bar */}
        <motion.div
          className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 text-sm"
          style={{ color: `${BRAND_COLORS.accent}60` }}
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
        >
          <p>&copy; {new Date().getFullYear()} HVME. All rights reserved.</p>
          <div className="flex items-center gap-6">
            {["Privacy", "Terms", "Cookies"].map((item) => (
              <motion.a
                key={item}
                href="#"
                className="transition-colors duration-300"
                whileHover={{ y: -2 }}
                onHoverStart={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.color = BRAND_COLORS.primary;
                }}
                onHoverEnd={(e) => {
                  const target = e.target as HTMLElement;
                  target.style.color = "";
                }}
              >
                {item}
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      <BackToTopButton />
    </footer>
  );
}
